import { Component, DestroyRef, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, Validators, ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { UnsavedChanges, UnsavedChangesService } from '../../routes/routes.guard';
import { debounceTime, map, Observable } from 'rxjs';
import { ScholarshipDetails } from '../../classes/scholarship-details';
import { School } from '../../classes/school';
import { AppService } from '../../services/app.service';
import { UserInfo, UserService } from '../../services/user.service';
import { validateDecimal } from '../../validators/decimal.validator';
import { validateNumber } from '../../validators/number.validator';
import { EditSchoolsModalComponent } from '../modals/edit-schools-modal/edit-schools-modal.component';
import { EditGeneral } from '../../classes/edit-general';
import { EditContacts } from '../../classes/edit-contacts';
import { EditSchools } from '../../classes/edit-schools';
import { EditAdditionals } from '../../classes/edit-additionals';
import { STATES } from '../../classes/states';
import { validateZipCode } from '../../validators/zipcode.validator';
import { EditRequirements } from '../../classes/edit-requirements';
import { ConfirmationDialogService } from '@polk_county_public_schools/ngx-pcps-dialogs/confirmation';
import { MatRadioModule } from '@angular/material/radio';
import { MatInputModule } from '@angular/material/input';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDividerModule } from '@angular/material/divider';
import { MatCardModule } from '@angular/material/card';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { SnackBarService } from '@polk_county_public_schools/ngx-pcps-dialogs/snackbar';
import { ErrorService } from '../../services/error.service';

@Component({
    selector: 'app-edit-scholarship',
    templateUrl: './edit-scholarship.component.html',
    styleUrls: ['./edit-scholarship.component.scss'],
    imports: [
        MatRadioModule,
        ReactiveFormsModule,
        MatInputModule,
        MatTabsModule,
        MatDividerModule,
        MatCardModule,
        MatSelectModule,
        MatButtonModule,
        RouterModule
    ]
})
export class EditScholarshipComponent implements OnInit, OnDestroy, UnsavedChanges {
    protected readonly destroyRef = inject(DestroyRef);
    protected readonly appSvc = inject(AppService);
    protected readonly confirmDlgSvc = inject(ConfirmationDialogService);
    protected readonly matDialog = inject(MatDialog);
    protected readonly route = inject(ActivatedRoute);
    protected readonly snackBarSvc = inject(SnackBarService);
    protected readonly unsavedChangesSvc = inject(UnsavedChangesService);
    protected readonly errorSvc = inject(ErrorService);
    protected readonly userSvc = inject(UserService);
    user: UserInfo;
    allStates = STATES;
    loading: boolean = true;
    scholarship: ScholarshipDetails;
    masterID: number;
    allSchools: School[];
    selectedSchools: School[];
    schoolsForm: FormGroup;
    schoolsEdited: boolean = false;
    schoolsSubmitted: boolean = false;
    schoolsValid: boolean = true;
    requirementsForm: FormGroup;
    requirementsEdited: boolean = false;
    requirementsSubmitted: boolean = false;
    requirementsValid: boolean = true;
    requirementsBaseline: string;
    generalForm: FormGroup;
    generalEdited: boolean = false;
    generalSubmitted: boolean = false;
    generalValid: boolean = true;
    generalBaseline: string;
    contactForm: FormGroup;
    contactEdited: boolean = false;
    contactSubmitted: boolean = false;
    contactValid: boolean = true;
    contactBaseline: string;
    additionalForm: FormGroup;
    additionalEdited: boolean = false;
    additionalSubmitted: boolean = false;
    additionalValid: boolean = true;
    additionalBaseline: string;
    changeTrackingActive = false;
    allBaseline: string = '';
    aQInvalid: boolean = false;

    ngOnInit() {
        this.user = this.userSvc.User;
        this.route.params
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((params) => {
                this.masterID = +params['id'];
                this.getScholarshipDetails();
            });
    }

    ngOnDestroy(): void {
        this.takeDownTracking();
    }

    setupTracking(): void {
        if (!this.changeTrackingActive) {
            this.unsavedChangesSvc.SetTracking(
                this.canDeactivate.bind(this),
                this.hasChanges.bind(this)
            );
            this.changeTrackingActive = true;
            this.allBaseline = this.getApplicationCompareJSON();
        }
    }

    canDeactivate(liveCheck?: boolean): boolean | Observable<boolean> | Promise<boolean> {
        // Are there unsaved changes in the current form, or are we not actively change tracking?
        if (!this.changeTrackingActive || !this.hasChanges()) {
            // No. The user can deactivate at will.
            return true;
        }

        // If this is a live check (native from browser), forego the confirmaion dialog as the native browser will catch instead.
        if (liveCheck === true) {
            return false;
        }

        // Need to prompt.
        return this.confirmDlgSvc.Open({
            TitleText: 'Unsaved Changes Detected',
            MessageText:
                `The page currently has unsaved changes that have not yet been submitted. If you leave ` +
                `this page at this time, these unsaved changes <strong>will be lost</strong>. Do you wish to discard the changes ` +
                `and leave the page?`,
            OKText: 'Yes - Discard Changes',
            CancelText: 'No - Stay On Page',
            maxWidth: '600px',
        }).pipe(
            map((result) => {
                if (result) {
                    // The user agreed. Take down change tracking now so we won't get prompted again.
                    this.takeDownTracking();
                }

                return !!result;
            })
        );
    }

    hasChanges() {
        const current = this.getApplicationCompareJSON();

        return current !== this.allBaseline;
    }

    takeDownTracking(): void {
        this.unsavedChangesSvc.ClearTracking();
        this.changeTrackingActive = false;
    }

    getApplicationCompareJSON() {
        return JSON.stringify({
            Schools: this.selectedSchools,
            RequirementsForm: this.requirementsForm.getRawValue(),
            GeneralForm: this.generalForm.getRawValue(),
            ContactForm: this.contactForm.getRawValue(),
            SchoolsForm: this.schoolsForm.getRawValue(),
            AdditionalForm: this.additionalForm.getRawValue(),
        });
    }

    updateInitialForChangeTracking(section?: number) {
        let initCopy = JSON.parse(this.allBaseline);
        switch (section) {
            case 1: //requirements tab
                initCopy.RequirementsForm = this.requirementsForm.getRawValue();
                this.requirementsEdited = false;
                this.requirementsSubmitted = false;
                break;
            case 2: //general tab
                initCopy.GeneralForm = this.generalForm.getRawValue();
                this.generalEdited = false;
                this.generalSubmitted = false;
                break;
            case 3: //contact tab
                initCopy.ContactForm = this.contactForm.getRawValue();
                this.contactEdited = false;
                this.contactSubmitted = false;
                break;
            case 4: //schools tab
                initCopy.SchoolsForm = this.schoolsForm.getRawValue();
                initCopy.Schools = this.selectedSchools;
                this.schoolsEdited = false;
                this.schoolsSubmitted = false;
                break;
            case 5: //additional tab
                initCopy.AdditionalForm = this.additionalForm.getRawValue();
                this.additionalEdited = false;
                this.additionalSubmitted = false;
                break;
            default: // Entire setup.
                this.allBaseline = this.getApplicationCompareJSON();
                return;
        }
        this.allBaseline = JSON.stringify(initCopy);
    }

    getScholarshipDetails() {
        this.appSvc
            .GetScholarshipDetails(this.masterID)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    this.scholarship = res;
                    this.getSchools();
                },
                error: (error) => {
                    this.errorSvc.HandleServiceError(error);
                }
            });
    }

    createRequirementsForm() {
        let sch = this.scholarship;

        this.requirementsForm = new FormGroup({
            ScholarshipName: new FormControl(
                sch.ScholarshipName,
                [Validators.required, Validators.maxLength(200)],
            ),
            Description: new FormControl(
                sch.Description,
                [Validators.required, Validators.maxLength(3000)],
            ),
            Active: new FormControl(sch.Active),
            MinGPA: new FormControl(sch.MinGPA || '', validateDecimal),
            MinSAT: new FormControl(sch.MinSAT || '', validateNumber),
            MinACT: new FormControl(sch.MinACT || '', validateNumber),
            MinCLT: new FormControl(sch.MinCLT || '', validateNumber),
            MinCommunityService: new FormControl(
                sch.MinCommunityService,
                [Validators.required, validateNumber],
            ),
            MinWorkHours: new FormControl(sch.MinWorkHours || '', validateNumber),
            Advertised: new FormControl(sch.Advertised)
        });

        this.requirementsBaseline = JSON.stringify(this.requirementsForm.value);

        this.requirementsForm.statusChanges
            .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(500))
            .subscribe((status) => {
                this.requirementsValid = status === 'VALID';

                this.requirementsEdited =
                    this.requirementsBaseline !==
                    JSON.stringify(this.requirementsForm.value);
            });
    }

    createGeneralForm() {
        let sch = this.scholarship;

        this.generalForm = new FormGroup({
            AdultSchoolEligible: new FormControl(
                sch.AdultSchoolEligible,
                Validators.maxLength(100),
            ),
            TechTrainingUsageAllowed: new FormControl(
                sch.TechTrainingUsageAllowed,
                Validators.maxLength(100),
            ),
            HardCopyApp: new FormControl(sch.HardCopyApp, Validators.maxLength(200)),
            YearEstablished: new FormControl(sch.YearEstablished, Validators.maxLength(100)),
            ScholarshipType: new FormControl(sch.ScholarshipType, Validators.maxLength(20)),
            EndowedTotal: new FormControl(sch.EndowedTotal, Validators.maxLength(20)),
            HowScholarshipPaid: new FormControl(
                sch.HowScholarshipPaid,
                Validators.maxLength(300),
            ),
            CommitmentFormReceived: new FormControl(
                sch.CommitmentFormReceived,
                Validators.maxLength(20),
            ),
            DonorTermsFormReceived: new FormControl(
                sch.DonorTermsFormReceived,
                Validators.maxLength(20),
            ),
            NbrScholarshipsAwarded: new FormControl(
                sch.NbrScholarshipsAwarded,
                Validators.maxLength(100),
            ),
            AmountAwarded: new FormControl(sch.AmountAwarded, Validators.maxLength(100)),
            TotalAmountAvailable: new FormControl(
                sch.TotalAmountAvailable,
                Validators.maxLength(100),
            ),
            ScholarshipDistribution: new FormControl(
                sch.ScholarshipDistribution,
                Validators.maxLength(150),
            ),
            RenewalEligibilitySpecialRequirements: new FormControl(
                sch.RenewalEligibilitySpecialRequirements,
                Validators.maxLength(500),
            ),
            FinancialNeed: new FormControl(sch.FinancialNeed, Validators.maxLength(50)),
            StudentEmploymentNarrowing: new FormControl(
                sch.StudentEmploymentNarrowing,
                Validators.maxLength(200),
            ),
            DonorSpecficNarrowing: new FormControl(
                sch.DonorSpecficNarrowing,
                Validators.maxLength(1500),
            ),
            RankingPriorities: new FormControl(
                sch.RankingPriorities,
                Validators.maxLength(250),
            ),
            SelectionProcess: new FormControl(sch.SelectionProcess, Validators.maxLength(250)),
            InterviewsRequired: new FormControl(
                sch.InterviewsRequired,
                Validators.maxLength(50),
            ),
            NbrApplicantsToInterview: new FormControl(
                sch.NbrApplicantsToInterview,
                Validators.maxLength(100),
            ),
            InterviewScreeningNotes: new FormControl(
                sch.InterviewScreeningNotes,
                Validators.maxLength(250),
            ),
            AwardLetterInfoToInclude: new FormControl(
                sch.AwardLetterInfoToInclude,
                Validators.maxLength(200),
            ),
            SpecialNotes: new FormControl(sch.SpecialNotes, Validators.maxLength(1500)),
            TranscriptsNeededForRenewal: new FormControl(
                sch.TranscriptsNeededForRenewal,
                Validators.maxLength(25),
            ),
            DonorPresenting: new FormControl(sch.DonorPresenting, Validators.maxLength(150)),
            WhoWillPresent: new FormControl(sch.WhoWillPresent, Validators.maxLength(150)),
        });

        this.generalBaseline = JSON.stringify(this.generalForm.value);

        this.generalForm.statusChanges
            .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(500))
            .subscribe((status) => {
                this.generalValid = status === 'VALID';

                this.generalEdited =
                    this.generalBaseline !==
                    JSON.stringify(this.generalForm.value);
            });
    }

    createContactForm() {
        let sch = this.scholarship;

        this.contactForm = new FormGroup({
            DonorName: new FormControl(sch.DonorName, Validators.maxLength(200)),
            DonorAddress: new FormControl(sch.DonorAddress, Validators.maxLength(60)),
            DonorCity: new FormControl(sch.DonorCity, Validators.maxLength(30)),
            DonorState: new FormControl(sch.DonorState, Validators.maxLength(10)),
            DonorZip: new FormControl(sch.DonorZip, [validateZipCode]),
            DonorPhone: new FormControl(sch.DonorPhone, Validators.maxLength(100)),
            DonorEmail: new FormControl(sch.DonorEmail, Validators.maxLength(100)),
            AdditionalContact: new FormControl(
                sch.AdditionalContact,
                Validators.maxLength(150),
            ),
            SecondaryAddress: new FormControl(sch.SecondaryAddress, Validators.maxLength(60)),
            SecondaryCity: new FormControl(sch.SecondaryCity, Validators.maxLength(30)),
            SecondaryState: new FormControl(sch.SecondaryState, Validators.maxLength(10)),
            SecondaryZip: new FormControl(sch.SecondaryZip, [validateZipCode]),
            SecondaryEmail: new FormControl(sch.SecondaryEmail, Validators.maxLength(100)),
            SecondaryPhone: new FormControl(sch.SecondaryPhone, Validators.maxLength(100)),
        });

        this.contactBaseline = JSON.stringify(this.contactForm.value);

        this.contactForm.statusChanges
            .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(500))
            .subscribe((status) => {
                this.contactValid = status === 'VALID';

                this.contactEdited =
                    this.contactBaseline !==
                    JSON.stringify(this.contactForm.value);
            });
    }

    createSchoolsForm() {
        this.schoolsForm = new FormGroup({
            ApplicantPool: new FormControl(this.scholarship.ApplicantPool, Validators.maxLength(500))
        });

        this.schoolsForm.statusChanges
            .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(500))
            .subscribe((status) => {
                this.schoolsValid = status === 'VALID';

                this.schoolsEdited =
                    this.scholarship.ApplicantPool !== this.schoolsForm.get('ApplicantPool').value ||
                    this.selectedSchools !== this.scholarship.SchoolList;
            });
    }

    createAdditionalForm() {
        this.additionalForm = new FormGroup({
            AdditionalQuestion: new FormControl(
                this.scholarship.AdditionalQuestion,
                Validators.maxLength(1500),
            ),
            AqHaveYesNo: new FormControl(this.scholarship.AqHaveYesNo),
            AqYesRequired: new FormControl(this.scholarship.AqYesRequired),
            AqHaveText: new FormControl(this.scholarship.AqHaveText),
            EssaySubject: new FormControl(this.scholarship.EssaySubject, Validators.maxLength(1000)),
            EmployeeName: new FormControl(this.scholarship.EmployeeNameReq),
            EmployeeLocation: new FormControl(this.scholarship.EmployeeLocationReq),
            EmployeeSSN: new FormControl(this.scholarship.EmployeeSSNReq),
            StoreLocationNumber: new FormControl(this.scholarship.StoreLocationNumberReq),
            AccountName: new FormControl(this.scholarship.AccountNameReq),
            AccountNumber: new FormControl(this.scholarship.AccountNumberReq),
            ParentName: new FormControl(this.scholarship.ParentNameReq),
            ParentSSN: new FormControl(this.scholarship.ParentSSNReq),
            ParentOrganization: new FormControl(this.scholarship.ParentOrganizationReq),
            ParentDepartment: new FormControl(this.scholarship.ParentDepartmentReq),
            ScholarshipEssay: new FormControl(this.scholarship.ScholarshipEssayReq),
            ScholarshipEssay2: new FormControl(this.scholarship.ScholarshipEssay2Req),
        });

        if (this.scholarship.AqHaveYesNo === true) {
            this.additionalForm.get('AqYesRequired').enable();
        }
        else {
            this.additionalForm.get('AqYesRequired').disable();
        }

        this.additionalForm.get('AqHaveYesNo').valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(val => {
            if (val === true) {
                this.additionalForm.get('AqYesRequired').enable();
            }
            else {
                this.additionalForm.get('AqYesRequired').disable();
            }
        });

        this.additionalBaseline = JSON.stringify(this.additionalForm.value);

        this.additionalForm.statusChanges
            .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(500))
            .subscribe((status) => {
                this.additionalValid = status === 'VALID';

                this.additionalEdited =
                    this.additionalBaseline !==
                    JSON.stringify(this.additionalForm.value);
            });

        this.loading = false;
    }

    getSchools() {
        this.appSvc
            .GetSchools()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    this.allSchools = res;
                    this.selectedSchools = this.scholarship.SchoolList;
                    this.createRequirementsForm();
                    this.createGeneralForm();
                    this.createContactForm();
                    this.createSchoolsForm();
                    this.createAdditionalForm();
                    this.setupTracking();
                },
                error: (error) => {
                    this.errorSvc.HandleServiceError(error);
                }
            });
    }

    editSchools() {
        let dialog = this.matDialog.open<EditSchoolsModalComponent>(
            EditSchoolsModalComponent,
            {
                disableClose: true,
                data: {
                    selectedSchools: this.selectedSchools,
                    allSchools: this.allSchools,
                    scholarshipName: this.scholarship.ScholarshipName,
                },
                width: '60%',
            }
        );

        dialog
            .afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((res: School[]) => {
                if (res == null) {
                    return;
                }

                this.selectedSchools = res;
                this.schoolsEdited =
                    this.scholarship.ApplicantPool !== this.schoolsForm.get('ApplicantPool').value ||
                    this.selectedSchools !== this.scholarship.SchoolList;
            });
    }

    requirementsSave(form: FormGroup) {
        this.requirementsSubmitted = true;

        if (form.invalid) {
            this.requirementsValid = false;
            return;
        }

        if (this.requirementsEdited === false) {
            return;
        }

        let edit: EditRequirements = form.value;
        edit.MasterID = this.masterID;

        this.appSvc
            .EditRequirements(edit)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    this.snackBarSvc.success('Submit Successful', 5000);
                    this.updateInitialForChangeTracking(1);
                },
                error: (error) => {
                    this.errorSvc.HandleServiceError(error);
                }
            });
    }

    generalSave(form: FormGroup) {
        this.generalSubmitted = true;

        if (form.invalid) {
            this.generalValid = false;
            return;
        }

        if (this.generalEdited === false) {
            return;
        }

        let edit: EditGeneral = form.value;
        edit.MasterID = this.masterID;

        this.appSvc
            .EditGeneral(edit)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    this.snackBarSvc.success('Submit Successful', 5000);
                    this.updateInitialForChangeTracking(2);
                },
                error: (error) => {
                    this.errorSvc.HandleServiceError(error);
                }
            });
    }

    contactSave(form: FormGroup) {
        this.contactSubmitted = true;

        if (form.invalid) {
            this.contactValid = false;
            return;
        }

        if (this.contactEdited === false) {
            return;
        }

        let edit: EditContacts = form.value;
        edit.MasterID = this.masterID;

        this.appSvc
            .EditContacts(edit)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    this.snackBarSvc.success('Submit Successful', 5000);
                    this.updateInitialForChangeTracking(3);
                },
                error: (error) => {
                    this.errorSvc.HandleServiceError(error);
                }
            });
    }

    schoolsSave(form: FormGroup) {
        this.schoolsSubmitted = true;

        if (form.invalid) {
            this.schoolsValid = false;
            return;
        }

        if (this.schoolsEdited === false) {
            return;
        }

        let edit: EditSchools = {
            ScholarshipID: this.scholarship.ScholarshipID,
            SchoolList: this.selectedSchools,
            ApplicantPool: form.value.ApplicantPool
        };

        this.appSvc
            .EditSchools(edit)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    this.snackBarSvc.success('Submit Successful', 5000);
                    this.updateInitialForChangeTracking(4);
                },
                error: (error) => {
                    this.errorSvc.HandleServiceError(error);
                }
            });
    }

    additionalSave(form: FormGroup) {
        this.additionalSubmitted = true;

        if (form.invalid) {
            this.additionalValid = false;
            return;
        }

        if (this.additionalEdited === false) {
            return;
        }

        let edit: EditAdditionals = form.value;
        edit.MasterID = this.masterID;

        if (edit.AdditionalQuestion != null && edit.AdditionalQuestion.trim() != '') {
            if (edit.AqHaveYesNo !== true && edit.AqHaveText != true) {
                this.aQInvalid = true;
                return;
            }
        }

        this.appSvc
            .EditAdditionals(edit)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    this.snackBarSvc.success('Submit Successful', 5000);
                    this.updateInitialForChangeTracking(5);
                },
                error: (error) => {
                    this.errorSvc.HandleServiceError(error);
                }
            });
    }
}
